/**
 * React Query hooks for Usage Dashboard
 */

import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useIsSystemAdmin } from '@/components/SystemAdminGuard';
import { useTenant } from '@/contexts/TenantContext';
import type { DateRange, UsageDashboardData, SystemUsageDashboardData, UsageStats, UsageByModel, UsageByAgent } from '../types';

/** Add computed total_tokens to model entries */
function withTotalTokens<T extends { input_tokens: number; output_tokens: number }>(items: T[]): (T & { total_tokens: number })[] {
  return items.map(item => ({ ...item, total_tokens: item.input_tokens + item.output_tokens }));
}

/**
 * Fetch complete usage dashboard data.
 * System admins get cross-tenant data via /admin/usage/dashboard,
 * optionally filtered by tenant (REQ-ANL-001 / ISS-330).
 * Regular tenants get tenant-scoped data via /llm/usage/dashboard.
 */
export function useUsageDashboard(
  dateRange: DateRange,
  tenantFilter: string | null = null,
  tenantOnly = false,
  enabled = true,
) {
  const isSystemAdmin = useIsSystemAdmin();
  const { currentTenant, isLoading: tenantLoading } = useTenant();

  return useQuery({
    queryKey: [
      'usage-dashboard',
      isSystemAdmin ? 'system' : 'tenant',
      tenantFilter ?? 'all',
      dateRange.from.toISOString(),
      dateRange.to.toISOString(),
    ],
    enabled: enabled && !tenantLoading && (isSystemAdmin || !!currentTenant),
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: dateRange.from.toISOString(),
        end_date: dateRange.to.toISOString(),
      });
      if (tenantFilter) {
        params.set('tenant_id', tenantFilter);
      }

      if (isSystemAdmin && !tenantOnly) {
        const { data } = await apiClient.get<SystemUsageDashboardData>(`/api/v1/admin/usage/dashboard?${params}`);
        return {
          ...data,
          by_model: withTotalTokens(data.by_model),
          by_tenant: withTotalTokens(data.by_tenant),
          by_agent: [] as (UsageByAgent & { total_tokens: number })[],
          isSystemView: true as const,
        };
      }

      const { data } = await apiClient.get<UsageDashboardData>(`/api/v1/llm/usage/dashboard?${params}`);
      return {
        ...data,
        by_model: withTotalTokens(data.by_model),
        by_agent: withTotalTokens(data.by_agent),
        by_tenant: [] as { tenant_id: string; tenant_name: string; input_tokens: number; output_tokens: number; total_tokens: number; request_count: number }[],
        isSystemView: false as const,
      };
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch usage stats only
 */
export function useUsageStats(dateRange: DateRange) {
  return useQuery({
    queryKey: ['usage-stats', dateRange.from.toISOString(), dateRange.to.toISOString()],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: dateRange.from.toISOString(),
        end_date: dateRange.to.toISOString(),
      });
      const { data } = await apiClient.get<UsageStats>(`/api/v1/llm/usage/stats?${params}`);
      return data;
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch usage breakdown by model
 */
export function useUsageByModel(dateRange: DateRange) {
  return useQuery({
    queryKey: ['usage-by-model', dateRange.from.toISOString(), dateRange.to.toISOString()],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: dateRange.from.toISOString(),
        end_date: dateRange.to.toISOString(),
      });
      const { data } = await apiClient.get<UsageByModel[]>(`/api/v1/llm/usage/by-model?${params}`);
      return withTotalTokens(data);
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch usage breakdown by agent
 */
export function useUsageByAgent(dateRange: DateRange) {
  return useQuery({
    queryKey: ['usage-by-agent', dateRange.from.toISOString(), dateRange.to.toISOString()],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: dateRange.from.toISOString(),
        end_date: dateRange.to.toISOString(),
      });
      const { data } = await apiClient.get<UsageByAgent[]>(`/api/v1/llm/usage/by-agent?${params}`);
      return withTotalTokens(data);
    },
    staleTime: 60 * 1000,
  });
}
